/**
 * Perfused volume model for AI Dosimetry workflow.
 *
 * A perfused volume represents a user-defined selection of Couinaud liver
 * segments (and optionally tumors) targeted for radioembolization treatment.
 */

/**
 * Perfused volume definition.
 */
export interface IPerfusedVolume {
    /** Unique identifier */
    readonly id: string;
    /** Display name (smart-named or user-edited) */
    readonly name: string;
    /** Selected Couinaud segment labelValues (1–8) */
    readonly segmentIndices: readonly number[];
    /** Selected tumor labelValues */
    readonly tumorIndices: readonly number[];
    /** Union volume of selected segments and tumors in mL (no double-counting of overlapping voxels) */
    readonly volumeMl: number;
    /** Sum of selected tumor volumes in mL */
    readonly tumorVolumeMl: number;
    /** Hepatic reserve percentage */
    readonly hepaticReservePercent: number;
}

// ============================================================================
// Smart Naming
// ============================================================================

/**
 * Known anatomical groupings mapped to their Couinaud segment indices (sorted).
 * Order matters: more specific combinations are checked first.
 */
const NAMED_COMBINATIONS: readonly { readonly segments: readonly number[]; readonly name: string }[] = [
    { segments: [1, 2, 3, 4, 5, 6, 7, 8], name: 'Whole Liver' },
    { segments: [1, 4, 5, 6, 7, 8], name: 'Right Lobe' },
    { segments: [5, 6, 7, 8], name: 'Right Liver' },
    { segments: [2, 3, 4], name: 'Left Liver' },
    { segments: [5, 8], name: 'Right Anterior' },
    { segments: [6, 7], name: 'Right Posterior' },
    { segments: [2, 3], name: 'Left Lateral' },
    { segments: [4], name: 'Left Medial' },
];

/** Roman numeral representation for segment indices 1–8. */
const ROMAN_NUMERALS: Readonly<Record<number, string>> = {
    1: 'I',
    2: 'II',
    3: 'III',
    4: 'IV',
    5: 'V',
    6: 'VI',
    7: 'VII',
    8: 'VIII',
};

/**
 * Generate a smart name for a perfused volume based on selected segment indices.
 *
 * If the selection matches a known anatomical grouping, that name is used.
 * Otherwise, falls back to "Segments {roman numerals}".
 *
 * @param segmentIndices - Selected segment labelValues (1–8), any order
 * @returns Human-readable name
 */
export function getSmartName(segmentIndices: readonly number[]): string {
    if (segmentIndices.length === 0) return '';

    const sorted = [...segmentIndices].sort((a, b) => a - b);

    for (const combo of NAMED_COMBINATIONS) {
        if (combo.segments.length === sorted.length && combo.segments.every((s, i) => s === sorted[i])) {
            return combo.name;
        }
    }

    const romans = sorted.map((s) => ROMAN_NUMERALS[s] ?? String(s));
    return `Segments ${romans.join(', ')}`;
}

// ============================================================================
// Hepatic Reserve
// ============================================================================

/**
 * Compute hepatic reserve as a percentage.
 *
 * Hepatic reserve = (totalLiverVolume - perfusedVolume) / totalLiverVolume * 100
 *
 * @param totalLiverVolumeMl - Total liver volume (sum of all 8 segments) in mL
 * @param perfusedVolumeMl - Perfused volume in mL
 * @returns Hepatic reserve percentage (0–100), or 0 if totalLiverVolume is 0
 */
export function computeHepaticReserve(totalLiverVolumeMl: number, perfusedVolumeMl: number): number {
    if (totalLiverVolumeMl <= 0) return 0;
    return ((totalLiverVolumeMl - perfusedVolumeMl) / totalLiverVolumeMl) * 100;
}

/** Hepatic reserve threshold: critical (red) */
export const HEPATIC_RESERVE_CRITICAL = 30;
/** Hepatic reserve threshold: warning (orange) */
export const HEPATIC_RESERVE_WARNING = 35;

/**
 * Get the status level for a hepatic reserve value.
 *
 * @param reservePercent - Hepatic reserve percentage
 * @returns 'critical' if < 30%, 'warning' if < 35%, 'normal' otherwise
 */
export function getHepaticReserveStatus(reservePercent: number): 'critical' | 'warning' | 'normal' {
    if (reservePercent < HEPATIC_RESERVE_CRITICAL) return 'critical';
    if (reservePercent < HEPATIC_RESERVE_WARNING) return 'warning';
    return 'normal';
}
